import { Col, Input, Row, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ChatHeader, ImageModal, Messages } from './components';
import { formatMessages } from './lib';

const Wrapper = styled.div`
  width: 560px;
  height: 100%;
  background-color: #fff;
  border-top: 1px solid ${theme.GREY_20};

  ${(props) =>
    props.isMobile &&
    `
    position: flex;
    width: 100vw;
    height: calc(100vh - 56px);
    z-index: 3;
  `}
`;

const Scrollable = styled.div`
  width: 100%;
  height: calc(100% - 64px);
  overflow-y: scroll;
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }

  padding: 0 0 16px 0;
  display: flex;
  justify-content: flex-start;
  flex-direction: column-reverse;

  ${(props) =>
    props.isMobile &&
    `
    height: calc(100vh - 268px)
  `}
`;

const ChatScreen = ({ data, user, setSelectedChat }) => {
  const [chat, setChat] = useState(null),
    [body, setBody] = useState(''),
    [searching, setSearching] = useState(false),
    [formatted, setFormatted] = useState([]),
    [searchResults, setSearchResults] = useState([]),
    [loading, setLoading] = useState(true),
    [image, setImage] = useState(null),
    currentUser = useSelector((state) => state.user),
    { isMobile } = useSelector((state) => state.appState),
    chatData = useSelector((state) => state.chats).find((x) => x._id === chat?._id),
    navigate = useNavigate(),
    dispatch = useDispatch(),
    { t } = useTranslation(),
    chatSettings = chat?.users?.find((x) => x.user._id === currentUser._id),
    otherUser = chat
      ? !chat?.group && chat.users.find((x) => x.user._id !== currentUser._id)?.user
      : user,
    needSub = currentUser.type === 'fan' && ['athlete', 'team'].includes(otherUser?.type),
    canMessage = needSub ? currentUser?.subscribed?.find((x) => x.user === otherUser._id) : true;

  useEffect(() => {
    if (!user) return;

    getChat();
  }, []);

  useEffect(() => {
    setChat(data);
    getChat();
  }, [data?._id, user]);

  useEffect(() => {
    if (!chatData?.messages?.length) return;

    setChat(chatData);
  }, [chatData]);

  useEffect(() => {
    if (searching) return;

    formatMessages({
      data: chat?.messages,
      chatSettings,
      setFormatted,
      setLoading,
      setSearchResults,
      searching
    });
  }, [chat?.messages, searching, chatSettings]);

  async function send() {
    setBody('');

    dispatch({
      type: 'NEW_MESSAGE',
      payload: {
        message: {
          body,
          createdAt: new Date().toString(),
          sender: currentUser._id
        },
        chat
      }
    });

    const res = await Api.post('/chats/send', { body, chatId: chat._id });

    if (!res) return;
  }

  async function markAsRead(chatId) {
    const res = await Api.post('/chats/markOneRead', { chatId });
    if (!res) return;

    dispatch({
      type: 'MARK_ONE_CHAT_READ',
      payload: {
        _id: chatId,
        userId: currentUser._id
      }
    });
  }

  async function getChat() {
    if (!chat?.messages) setLoading(true);

    const query = {
      chatId: data?._id,
      userId: user
    };

    if (!query.chatId && !query.userId) return;

    const res = await Api.post('/chats/get', query);
    if (!res) return;

    const newChat = {
      ...res.chat,
      users: res.chat.users.map((x) => {
        if (x.user !== currentUser._id) return x;

        return {
          ...x,
          user: {
            _id: currentUser._id,
            name: currentUser.name,
            username: currentUser.username,
            avatar: currentUser.avatar
          }
        };
      }),
      messages: res.messages
    };

    setLoading(!!res.messages?.length);
    setChat(newChat);

    dispatch({
      type: 'SET_CHAT',
      payload: newChat
    });

    markAsRead(newChat._id);
  }

  function removeSelection() {
    setChat(null);
    setSelectedChat(null);
    navigate('/messages');
  }

  function handleImage(data) {
    setImage(data);
  }

  function handleChange({ target: { value } }) {
    if (value.includes('fakepath')) return;

    setBody(value);
  }

  if (isMobile && !chat) return null;

  if (!chat)
    return (
      <Row pos={isMobile ? 'absolute' : 'unset'} zIndex={2}>
        <Wrapper isMobile={isMobile}>
          <Col centerAll ht="100%">
            <Text h2 col={theme.GREY_50}>
              {t('messages.noChatSelected')}
            </Text>
          </Col>
        </Wrapper>
      </Row>
    );

  if (loading)
    return (
      <Row pos={isMobile ? 'absolute' : 'unset'} zIndex={2}>
        <Wrapper isMobile={isMobile}>
          <Col wid="100%" ht="calc(100vh - 74px)" noFlex centerAll>
            <Spinner2 col={theme.SECONDARY} />
          </Col>
        </Wrapper>
      </Row>
    );

  return (
    <Row
      pos={isMobile ? 'absolute' : 'unset'}
      zIndex={2}
      ht={isMobile ? 'calc(100vh - 118px);' : 'auto'}
    >
      <ImageModal
        visible={image}
        closeModal={() => setImage(null)}
        chat={chat}
        currentUserId={currentUser._id}
        dispatch={dispatch}
        isMobile={isMobile}
        preview
      />

      <Wrapper isMobile={isMobile}>
        <Row ht="71px" wid="100%" borderBottom={`1px solid ${theme.GREY_20}`}>
          <ChatHeader chat={chat} removeSelection={removeSelection} />
        </Row>

        <Col between ht="calc(100% - 72px)">
          <Scrollable isMobile={isMobile}>
            <Messages data={formatted} />
          </Scrollable>

          <Row wid="100%" ht="63px" borderTop={`1px solid ${theme.GREY_20}`}>
            {canMessage ? (
              <Input
                value={body}
                onChange={handleChange}
                handleImage={handleImage}
                send={send}
                chat
                marg="8px 0"
                pad="0 16px"
                wid={isMobile ? '100%' : '496px'}
                placeholder={t('messages.messageDotDotDot')}
              />
            ) : (
              <Text b2 med marg="12px 16px 0" center wid="100%">
                You need to subscribe to {otherUser.name} to send a message
              </Text>
            )}
          </Row>
        </Col>
      </Wrapper>
    </Row>
  );
};

export default ChatScreen;
